// modules/reporting_dashboard/src/components/Settings/DatabaseManagement.tsx

import React, { useState, useEffect } from 'react';
import './DatabaseManagement.css';

interface DatabaseInfo {
    instance_name: string;
    tier: string;
    state: string;
    is_running: boolean;
    max_connections: number;
    monthly_cost_usd: number;
    region: string;
    database_version: string;
    disk_size_gb: string;
    environment: string;
    can_manage: boolean;
    message?: string;
}

interface SystemMode {
    mode: string;
    is_demo: boolean;
    is_production: boolean;
    can_delete_database: boolean;
    environment: string;
}

interface DatabaseManagementProps {
    apiBaseUrl: string;
}

const DatabaseManagement: React.FC<DatabaseManagementProps> = ({ apiBaseUrl }) => {
    const [dbInfo, setDbInfo] = useState<DatabaseInfo | null>(null);
    const [systemMode, setSystemMode] = useState<SystemMode | null>(null);
    const [loading, setLoading] = useState(true);
    const [operation, setOperation] = useState<string | null>(null);

    useEffect(() => {
        loadDatabaseInfo();
        loadSystemMode();
    }, []);

    const loadDatabaseInfo = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/system/database/status`);
            const data = await response.json();
            if (data.status === 'success') {
                setDbInfo(data.database);
            }
        } catch (error) {
            console.error('Failed to load database info:', error);
        }
    };

    const loadSystemMode = async () => {
        try {
            const response = await fetch(`${apiBaseUrl}/system/mode`);
            const data = await response.json();
            setSystemMode(data);
        } catch (error) {
            console.error('Failed to load system mode:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleMode = async () => {
        const newMode = systemMode?.is_demo ? 'production' : 'demo';
        if (!window.confirm(`Switch to ${newMode} mode?`)) return;

        setOperation('mode');
        try {
            const response = await fetch(`${apiBaseUrl}/system/mode`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode: newMode })
            });
            const data = await response.json();
            if (data.status === 'success') {
                await loadSystemMode();
                alert(`Switched to ${newMode} mode`);
            } else {
                alert(data.message || 'Failed to change mode');
            }
        } catch (error) {
            alert('Failed to change mode');
        } finally {
            setOperation(null);
        }
    };

    const changeTier = async (newTier: string) => {
        if (!window.confirm(`Change database tier to ${newTier}? This will take 5-10 minutes.`)) return;

        setOperation('tier');
        try {
            const response = await fetch(`${apiBaseUrl}/system/database/tier`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tier: newTier,
                    confirm_production: systemMode?.is_production
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                alert('Database tier change initiated. This will take 5-10 minutes.');
                // Refresh status after delay
                setTimeout(loadDatabaseInfo, 5000);
            } else {
                alert(data.message || 'Failed to change tier');
            }
        } catch (error) {
            alert('Failed to change tier');
        } finally {
            setOperation(null);
        }
    };

    const toggleDatabaseState = async () => {
        const action = dbInfo?.is_running ? 'stop' : 'start';
        const message = dbInfo?.is_running
            ? 'Stop database? Services will be unavailable.'
            : 'Start database? This takes 2-3 minutes.';

        if (!window.confirm(message)) return;

        setOperation('state');
        try {
            const response = await fetch(`${apiBaseUrl}/system/database/state`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action,
                    confirm_production: systemMode?.is_production
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                alert(`Database ${action} initiated`);
                setTimeout(loadDatabaseInfo, 3000);
            } else {
                alert(data.message || `Failed to ${action} database`);
            }
        } catch (error) {
            alert(`Failed to ${action} database`);
        } finally {
            setOperation(null);
        }
    };

    const deleteDatabase = async () => {
        // Triple confirmation for safety
        if (!window.confirm('⚠️ DELETE DATABASE? This action CANNOT be undone!')) return;
        if (!window.confirm('Are you ABSOLUTELY sure? All data will be permanently lost!')) return;

        const instanceName = window.prompt('Type "testwerk-db-main" to confirm deletion:');
        if (instanceName !== 'testwerk-db-main') {
            alert('Deletion cancelled - instance name mismatch');
            return;
        }

        setOperation('delete');
        try {
            const response = await fetch(`${apiBaseUrl}/system/database`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    confirm_delete: 'DELETE_DATABASE_PERMANENTLY',
                    instance_name: 'testwerk-db-main'
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                alert('Database deletion initiated. Run deploy.sh to recreate.');
            } else {
                alert(data.message || 'Failed to delete database');
            }
        } catch (error) {
            alert('Failed to delete database');
        } finally {
            setOperation(null);
            loadDatabaseInfo();
        }
    };

    if (loading) return <div>Loading database management...</div>;

    const isLocal = dbInfo?.environment === 'local';
    const canManage = dbInfo?.can_manage && !isLocal;

    return (
        <div className="database-management">
            <h3>Database Management</h3>

            {/* Current Status */}
            <div className="db-status-card">
                <h4>Current Status</h4>
                <div className="status-grid">
                    <div>Instance: <strong>{dbInfo?.instance_name}</strong></div>
                    <div>Tier: <strong>{dbInfo?.tier}</strong></div>
                    <div>State: <strong className={dbInfo?.is_running ? 'running' : 'stopped'}>
                        {dbInfo?.state}
                    </strong></div>
                    <div>Connections: <strong>{dbInfo?.max_connections}</strong></div>
                    <div>Cost: <strong>${dbInfo?.monthly_cost_usd}/month</strong></div>
                    <div>Region: <strong>{dbInfo?.region}</strong></div>
                </div>
            </div>

            {/* Mode Toggle */}
            <div className="mode-section">
                <h4>Deployment Mode</h4>
                <div className="mode-toggle">
                    <span className={systemMode?.is_demo ? 'active' : ''}>Demo</span>
                    <label className="switch">
                        <input
                            type="checkbox"
                            checked={systemMode?.is_production || false}
                            onChange={toggleMode}
                            disabled={!canManage || operation !== null}
                        />
                        <span className="slider"></span>
                    </label>
                    <span className={systemMode?.is_production ? 'active' : ''}>Production</span>
                </div>
                {systemMode?.is_production && (
                    <div className="warning-banner">
                        ⚠️ Production Mode - Delete operations disabled
                    </div>
                )}
            </div>

            {/* Management Controls */}
            {canManage ? (
                <>
                    <div className="control-section">
                        <h4>Database Tier</h4>
                        <div className="tier-buttons">
                            <button
                                className={`tier-btn ${dbInfo?.tier === 'db-f1-micro' ? 'active' : ''}`}
                                onClick={() => changeTier('db-f1-micro')}
                                disabled={operation !== null || dbInfo?.tier === 'db-f1-micro'}
                            >
                                f1-micro
                                <span>Shared • 0.6GB RAM • 25 conn • $7.56/mo</span>
                            </button>
                            <button
                                className={`tier-btn ${dbInfo?.tier === 'db-g1-small' ? 'active' : ''}`}
                                onClick={() => changeTier('db-g1-small')}
                                disabled={operation !== null || dbInfo?.tier === 'db-g1-small'}
                            >
                                g1-small
                                <span>Shared • 1.7GB RAM • 50 conn • $25.20/mo</span>
                            </button>
                            <button
                                className={`tier-btn ${dbInfo?.tier === 'db-custom-1-3840' ? 'active' : ''}`}
                                onClick={() => changeTier('db-custom-1-3840')}
                                disabled={operation !== null || dbInfo?.tier === 'db-custom-1-3840'}
                            >
                                custom-1-3840
                                <span>1 vCPU • 3.75GB RAM • 100 conn • $35/mo</span>
                            </button>
                            <button
                                className={`tier-btn ${dbInfo?.tier === 'db-custom-2-7680' ? 'active' : ''}`}
                                onClick={() => changeTier('db-custom-2-7680')}
                                disabled={operation !== null || dbInfo?.tier === 'db-custom-2-7680'}
                            >
                                custom-2-7680
                                <span>2 vCPU • 7.5GB RAM • 200 conn • $70/mo</span>
                            </button>
                        </div>
                    </div>
                    {/*
                    <div className="control-section">
                        <h4>Database State</h4>
                        <button
                            className={`state-btn ${dbInfo?.is_running ? 'stop' : 'start'}`}
                            onClick={toggleDatabaseState}
                            disabled={operation !== null}
                        >
                            {dbInfo?.is_running ? '⏸️ Stop Database' : '▶️ Start Database'}
                        </button>
                        {!dbInfo?.is_running && (
                            <div className="info-banner">
                                Database stopped - saving ${dbInfo?.monthly_cost_usd}/month
                            </div>
                        )}
                    </div>

                    {systemMode?.is_demo && (
                        <div className="danger-zone">
                            <h4>⚠️ This action cannot be undone!</h4>
                            <button
                                className="delete-btn"
                                onClick={deleteDatabase}
                                disabled={operation !== null}
                            >
                                🗑️ Delete Database
                            </button>
                            <small>Admin Control Coming Soon</small>
                        </div>
                    )}
                        */}
                </>
            ) : (
                <div className="local-message">
                    {dbInfo?.message || 'Database management only available in Cloud Run deployment'}
                </div>
            )}

            {operation && (
                <div className="operation-overlay">
                    <div className="spinner"></div>
                    <p>Operation in progress: {operation}</p>
                </div>
            )}
        </div>
    );
};

export default DatabaseManagement;